import { z } from "zod";
import type { AppData } from "../domain/types";
import { normalizeAppData, serializeAppData, type PortableAppData } from "./storage";

const SYNC_IDENTITY_KEY = "haebolcae.sync-identity.v1";
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const toBase64Url = (bytes: Uint8Array) => {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
};

const fromBase64Url = (value: string) => {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(base64);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

const randomSecret = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
};

export const syncIdentitySchema = z.object({
  schemaVersion: z.literal(1),
  workspaceId: z.string().uuid(),
  accessToken: z.string().min(40),
  encryptionKey: z.string().min(40),
  revision: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});

export type SyncIdentity = z.infer<typeof syncIdentitySchema>;

export const encryptedEnvelopeSchema = z.object({
  schemaVersion: z.literal(1),
  algorithm: z.literal("A256GCM"),
  iv: z.string().min(12),
  ciphertext: z.string().min(16),
});

export type EncryptedEnvelope = z.infer<typeof encryptedEnvelopeSchema>;

export function createSyncIdentity(now = new Date()): SyncIdentity {
  return {
    schemaVersion: 1,
    workspaceId: crypto.randomUUID(),
    accessToken: randomSecret(),
    encryptionKey: randomSecret(),
    revision: 0,
    createdAt: now.toISOString(),
  };
}

async function aesKey(secret: string) {
  const bytes = fromBase64Url(secret);
  if (bytes.byteLength !== 32) throw new Error("복구 키 길이가 올바르지 않아요.");
  return crypto.subtle.importKey("raw", bytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export async function encryptAppData(data: AppData, secret: string): Promise<EncryptedEnvelope> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    await aesKey(secret),
    encoder.encode(serializeAppData(data)),
  );
  return {
    schemaVersion: 1,
    algorithm: "A256GCM",
    iv: toBase64Url(iv),
    ciphertext: toBase64Url(new Uint8Array(ciphertext)),
  };
}

export async function decryptAppData(envelope: EncryptedEnvelope, secret: string): Promise<AppData> {
  const parsed = encryptedEnvelopeSchema.parse(envelope);
  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64Url(parsed.iv) },
      await aesKey(secret),
      fromBase64Url(parsed.ciphertext),
    );
    return normalizeAppData(JSON.parse(decoder.decode(plaintext)) as PortableAppData);
  } catch {
    throw new Error("복구 키가 다르거나 백업이 손상됐어요.");
  }
}

export function loadSyncIdentity(): SyncIdentity | undefined {
  try {
    const raw = localStorage.getItem(SYNC_IDENTITY_KEY);
    return raw ? syncIdentitySchema.parse(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}

export function saveSyncIdentity(identity: SyncIdentity) {
  localStorage.setItem(SYNC_IDENTITY_KEY, JSON.stringify(syncIdentitySchema.parse(identity)));
}

export function deleteSyncIdentity() {
  localStorage.removeItem(SYNC_IDENTITY_KEY);
}

export function exportRecoveryKit(identity: SyncIdentity): Blob {
  return new Blob([JSON.stringify(syncIdentitySchema.parse(identity), null, 2)], { type: "application/json" });
}

type RemoteWorkspace = { revision: number; envelope: EncryptedEnvelope; updatedAt: string };

export class SyncConflictError extends Error {
  constructor(public readonly remoteRevision: number) {
    super("다른 기기에서 먼저 바뀌었어요. 최신 백업을 받은 뒤 다시 시도해 주세요.");
  }
}

export async function pushEncryptedWorkspace(input: {
  apiBase: string;
  identity: SyncIdentity;
  data: AppData;
  revision: number;
}): Promise<RemoteWorkspace> {
  if (!input.apiBase) throw new Error("암호화 동기화 서버가 아직 연결되지 않았어요.");
  const response = await fetch(`${input.apiBase.replace(/\/$/u, "")}/api/v1/workspaces/${input.identity.workspaceId}`, {
    method: "PUT",
    headers: { "content-type": "application/json", authorization: `Bearer ${input.identity.accessToken}` },
    body: JSON.stringify({ revision: input.revision, envelope: await encryptAppData(input.data, input.identity.encryptionKey) }),
  });
  const payload = await response.json() as RemoteWorkspace & { error?: string; message?: string };
  if (response.status === 409) throw new SyncConflictError(payload.revision);
  if (!response.ok) throw new Error(payload.message ?? "암호화 백업을 저장하지 못했어요.");
  return payload;
}

export async function pullEncryptedWorkspace(input: {
  apiBase: string;
  identity: SyncIdentity;
}): Promise<{ data: AppData; revision: number; updatedAt: string }> {
  if (!input.apiBase) throw new Error("암호화 동기화 서버가 아직 연결되지 않았어요.");
  const response = await fetch(`${input.apiBase.replace(/\/$/u, "")}/api/v1/workspaces/${input.identity.workspaceId}`, {
    headers: { authorization: `Bearer ${input.identity.accessToken}` },
  });
  const payload = await response.json() as RemoteWorkspace & { message?: string };
  if (!response.ok) throw new Error(payload.message ?? "암호화 백업을 불러오지 못했어요.");
  return {
    data: await decryptAppData(payload.envelope, input.identity.encryptionKey),
    revision: payload.revision,
    updatedAt: payload.updatedAt,
  };
}

export async function deleteRemoteWorkspace(input: { apiBase: string; identity: SyncIdentity }) {
  if (!input.apiBase) throw new Error("암호화 동기화 서버가 아직 연결되지 않았어요.");
  const response = await fetch(`${input.apiBase.replace(/\/$/u, "")}/api/v1/workspaces/${input.identity.workspaceId}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${input.identity.accessToken}` },
  });
  if (!response.ok && response.status !== 404) {
    const payload = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(payload.message ?? "서버 백업을 삭제하지 못했어요.");
  }
}
